import React, { useId, useState } from "react";
import styles from "./ChoixExplicite.module.css";

/**
 * Choix explicite à deux boutons de même poids (consentement, réglage proposé une fois) :
 * rien n'est coché d'avance, aucun bouton n'est mis en avant. Après le choix, le groupe
 * laisse place à une phrase de confirmation annoncée aux lecteurs d'écran.
 *
 * `onChoix` peut être asynchrone : en cas d'échec (exception), les deux boutons reviennent
 * avec le message `erreur`.
 */
export type ChoixExpliciteProps = {
  titre: string;
  texte?: React.ReactNode;
  oui: string;
  non: string;
  onChoix: (oui: boolean) => Promise<void> | void;
  confirmationOui: React.ReactNode;
  confirmationNon: React.ReactNode;
  erreur?: string;
  className?: string;
};

type Etat = "attente" | "envoi" | "oui" | "non" | "erreur";

export function ChoixExplicite({
  titre,
  texte,
  oui,
  non,
  onChoix,
  confirmationOui,
  confirmationNon,
  erreur = "Le réglage n'a pas pu être enregistré. Réessayez.",
  className,
}: ChoixExpliciteProps) {
  const id = useId();
  const [etat, setEtat] = useState<Etat>("attente");

  const choisir = async (valeur: boolean) => {
    setEtat("envoi");
    try {
      await onChoix(valeur);
      setEtat(valeur ? "oui" : "non");
    } catch {
      setEtat("erreur");
    }
  };

  if (etat === "oui" || etat === "non") {
    return (
      <p className={[styles.fait, className].filter(Boolean).join(" ")} role="status">
        {etat === "oui" ? confirmationOui : confirmationNon}
      </p>
    );
  }

  return (
    <div className={[styles.choix, className].filter(Boolean).join(" ")} role="group" aria-labelledby={`${id}-t`}>
      <p className={styles.titre} id={`${id}-t`}>{titre}</p>
      {texte && <p className={styles.texte}>{texte}</p>}
      <div className={styles.boutons}>
        <button type="button" className={styles.bouton} disabled={etat === "envoi"} onClick={() => void choisir(true)}>{oui}</button>
        <button type="button" className={styles.bouton} disabled={etat === "envoi"} onClick={() => void choisir(false)}>{non}</button>
      </div>
      {etat === "erreur" && <p className={styles.erreur} role="alert">{erreur}</p>}
    </div>
  );
}
